/* eslint-disable react/no-array-index-key */
import { useSelector } from "react-redux";
import { Box, Button } from "@mui/material";
import { useReactToPrint } from "react-to-print";
import React, { ReactNode, useRef } from "react";

import { RootState } from "../../../store";
import { tl } from "../../../components/translate";
import { InfoField } from "../../../components/Print/Print";
import { Client } from "../../../interfaces/ClientInterface";
import { separateByComma } from "../../../helpers/formatters";
import { clientFullNameSelector } from "../../../reducers/client";
import { Assessment } from "../../../interfaces/AssessmentInterfaces";
import { hasDiagnosis } from "../../Modals/Assessment/DiagnosisRenderer";
import AssessmentContent from "../../Modals/Assessment/AssessmentContent";
import { getGenderString } from "../../Campaign/Audience/AudienceHelpers";
import { filterEmptyTableData } from "../../../components/EditableTable/EditableTable";
import { formatAssessmentForPrint } from "../../Modals/Assessment/AssessmentComponents";
import LabPrintLetterHead from "../../Lab/LabPrint/LabprintComponents/LabPrintLetterhead";
import { hasContentAdvancedIntraOral } from "../../Modals/Assessment/AdvancedIntraOralContent";

// @ts-expect-error - image
import Logo from "../../../../assets/images/poweredByOkhati.png";

const hasRelevantData = (docs) => {
  if (!docs) return false;

  return (
    docs.prescriptionAudioUrl ||
    docs.symptoms?.length > 0 ||
    docs.pastHistoryOfAllergy ||
    docs.hopi ||
    docs.assessment ||
    docs.investigation ||
    (docs.intraOral &&
      (Object.keys(docs.intraOral.primary).length !== 0 ||
        Object.keys(docs.intraOral.permanent).length !== 0)) ||
    Object.values(docs.extraOral || {}).some(Boolean) ||
    (docs.advancedIntraOral && hasContentAdvancedIntraOral(docs.advancedIntraOral)) ||
    (docs.eye && Object.keys(docs.eye).length > 0) ||
    formatAssessmentForPrint(docs.questionnaire).length > 0 ||
    formatAssessmentForPrint(docs.vitals).length > 0 ||
    docs.provisionalDiagnosis ||
    [...(docs.tests || []), ...(docs.services || [])].length > 0 ||
    hasDiagnosis(docs.diagnosis) ||
    (docs.tabularComponent && filterEmptyTableData(docs.tabularComponent)) ||
    docs.presentHealthStatus ||
    docs.attachments?.length > 0 ||
    docs.treatmentPlan ||
    docs.treatmentGiven ||
    docs.medication?.length > 0 ||
    docs.productMedication?.length > 0 ||
    docs.followup ||
    docs.others ||
    docs.advice
  );
};

// client data component
const ClientData = ({ clientData }: { clientData: Client }) => (
  <Box
    sx={{
      padding: "10px",
      display: "flex",
      flexDirection: "column",
      border: "1px solid #bbbbbb",
      borderRadius: "3px"
    }}
  >
    <InfoField label={tl("name")} data={clientFullNameSelector(clientData)} />
    {clientData?.phone && <InfoField label={tl("phone")} data={clientData.phone} />}
    {clientData?.email && <InfoField label={tl("email")} data={clientData.email} />}
    {clientData?.gender && (
      <InfoField label={tl("gender")} data={getGenderString(Number(clientData.gender))} />
    )}
    {(clientData?.address || clientData?.city) && (
      <InfoField
        label={tl("clients.address")}
        data={separateByComma(clientData.address, clientData.city)}
      />
    )}
  </Box>
);

interface PropType {
  assessments: Assessment[];
  clientData: Client;
  loading: boolean;
}

export default function AssessmentSummaryPrint({
  assessments,
  clientData,
  loading
}: PropType): ReactNode {
  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({
    content: () => contentRef.current,
    pageStyle: `
      @page {
        margin: 0;
      }
    `
  });

  const resourceCentre = useSelector((state: RootState) => state.resources.resourceCentres[0]);
  const clinicNameFontScale = resourceCentre?.settings?.printSettings.clinicNameFontScale;
  const panNo = resourceCentre?.settings?.billingSettings?.panNo;

  const filteredAssessments = React.useMemo(
    () => assessments.filter((assmt) => hasRelevantData(assmt.assessmentDocument)),
    [assessments]
  );

  return (
    <div>
      <Button
        type="button"
        variant="outlined"
        onClick={() => reactToPrintFn()}
        loading={loading}
        disabled={loading || !assessments || assessments.length === 0}
      >
        Assessment Summary
      </Button>
      <Box
        ref={contentRef}
        sx={{
          display: "none",
          height: "100%",
          width: "100%",
          "@media print": {
            display: "flex",
            flexDirection: "column"
          }
        }}
      >
        <Box component="table" width="100%">
          <thead>
            <tr>
              <td>
                <Box sx={{ padding: "30px" }}>
                  <LabPrintLetterHead
                    resourceCentre={{
                      ...resourceCentre,
                      panNo
                    }}
                    titleFontScale={clinicNameFontScale}
                    hideRcLogo
                  />
                </Box>
              </td>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td>
                <Box
                  sx={{
                    padding: "0 30px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    "@media print": {
                      paddingBottom: "200px"
                    }
                  }}
                >
                  <ClientData clientData={clientData} />
                  {filteredAssessments.map((assmt: Assessment, index) => (
                    <Box
                      key={assmt.id}
                      sx={{
                        "@media print": {
                          pageBreakBefore: index === 0 ? "auto" : "always"
                        }
                      }}
                    >
                      <AssessmentContent
                        data={assmt}
                        assessmentDoc={assmt.assessmentDocument}
                        showClientData={false}
                        showAttachment={false}
                      />
                    </Box>
                  ))}
                </Box>
              </td>
            </tr>
          </tbody>

          {/* footer */}
          <tfoot>
            <tr>
              <td>
                <Box
                  component="div"
                  sx={{
                    height: "75px",
                    width: "100%",
                    display: "flex",
                    padding: "0 30px",
                    borderTop: "1px solid #c2c2c2",
                    position: "fixed",
                    bottom: 0,
                    backgroundColor: "#fff",
                    "@media print": {
                      position: "fixed",
                      bottom: 0,
                      left: 0,
                      right: 0
                    }
                  }}
                >
                  <Box
                    sx={{
                      marginLeft: "auto",
                      height: "100%",
                      display: "flex",
                      alignItems: "center"
                    }}
                  >
                    <img src={Logo} alt="logo" height="35px" />
                  </Box>
                </Box>
              </td>
            </tr>
          </tfoot>
        </Box>
      </Box>
    </div>
  );
}
